import useEmblaCarousel from 'embla-carousel-react';
import CarouselSlide from '@/components/newImageCarousel/CarouselSlide';
import CarouselThumbnail from '@/components/newImageCarousel/CarouselThumbnail';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import CarouselButton from '@/components/newImageCarousel/controls/CarouselButton';
import CarouselPages from '@/components/newImageCarousel/controls/CarouselPages';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';
import { ServerMediaAsset } from '@/lib/janitsjar/Product';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import Lightbox from '@/components/imageCarousel/Lightbox';
import { TMedia } from '@/components/imageCarousel';

const config = {
  desktop: {
    thumbnail_width: '115px',
    thumbnail_height: '96px',
  },
  mobile: {
    thumbnail_width: '72px',
    thumbnail_height: '62px',
  },
};

type NewImageCarouselProps = {
  media: ServerMediaAsset[];
} & PropsWithChildren;

export default function NewImageCarousel({
  media,
  children,
}: NewImageCarouselProps) {
  const isLg = useMediaQuery('(min-width: 1024px)');
  const currentConfig = isLg ? config.desktop : config.mobile;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [thumbRef, thumbApi] = useEmblaCarousel({
    dragFree: true,
    axis: isLg ? 'y' : 'x',
    align: 'center',
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const isFirstSlide = selectedIndex === 0;
  const isLastSlide = selectedIndex === media.length - 1;

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    if (thumbApi) thumbApi.scrollTo(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const goToSlide = (index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  };

  const nextSlide = () => {
    if (emblaApi) emblaApi.scrollNext();
  };

  const prevSlide = () => {
    if (emblaApi) emblaApi.scrollPrev();
  };

  const transformMedia = (m: ServerMediaAsset[]): TMedia[] => {
    return m.map((e) => {
      return {
        assetType: e.type,
        url: e.url,
        key: e.id,
      };
    });
  };

  const transformedMedia = transformMedia(media);
  const currentMedia = transformedMedia[selectedIndex];

  return (
    <div className="flex flex-row lg:flex-col gap-4 -mx-4 lg:mx-0">
      {expanded && (
        <Lightbox
          media={transformedMedia}
          currentMedia={currentMedia}
          onChangeMedia={(m) => {
            const newIndex = transformedMedia.findIndex((m2) => m2 === m);
            goToSlide(Math.max(newIndex, 0));
          }}
          onClose={() => {
            setExpanded(false);
          }}
        />
      )}
      <div
        className={twMerge(
          clsx('size-full grid gap-4 relative', {
            'grid-rows-[1fr_auto] lg:grid-rows-1 lg:grid-cols-[auto_1fr]':
              media.length > 1,
          })
        )}
      >
        {media.length > 1 && (
          <div
            className="overflow-hidden order-1 lg:-order-1 grid grid-cols-[auto_1fr_auto] lg:grid-cols-1 lg:grid-rows-1 gap-2 group"
            style={{ height: isLg ? 'auto' : currentConfig.thumbnail_height }}
          >
            {!isFirstSlide && (
              <CarouselButton
                handleClick={prevSlide}
                Icon={isLg ? ChevronUpIcon : ChevronLeftIcon}
                type="small"
                className="hidden lg:group-hover:flex absolute -top-4 left-[45px] z-20"
              />
            )}
            <div className="embla overflow-hidden" ref={thumbRef}>
              <div className="embla__container flex flex-row lg:flex-col gap-4 h-[340px] mx-4 lg:mx-0">
                {media.map((m, index) => (
                  <CarouselThumbnail
                    media={m}
                    key={index}
                    onClick={() => goToSlide(index)}
                    active={selectedIndex === index}
                    width={currentConfig.thumbnail_width}
                    height={currentConfig.thumbnail_height}
                  />
                ))}
              </div>
            </div>
            {!isLastSlide && (
              <CarouselButton
                handleClick={nextSlide}
                Icon={isLg ? ChevronDownIcon : ChevronRightIcon}
                type="small"
                className="hidden lg:group-hover:flex absolute -bottom-4 left-[45px]"
              />
            )}
          </div>
        )}
        <div className="w-full overflow-hidden relative -order-1 lg:order-1">
          <div
            className="embla overflow-hidden bg-neutral-30 lg:rounded w-full aspect-square"
            ref={emblaRef}
          >
            <div className="embla__container flex size-full">
              {media.map((m, index) => (
                <CarouselSlide
                  onClick={() => setExpanded(true)}
                  key={index}
                  media={m}
                  activeSlide={selectedIndex === index}
                />
              ))}
            </div>
          </div>
          {media.length > 1 && (
            <div className="absolute inset-0 flex justify-between items-center p-2 pointer-events-none">
              <CarouselButton
                handleClick={prevSlide}
                Icon={ChevronLeftIcon}
                type="large"
                className="pointer-events-auto"
              />

              <CarouselButton
                handleClick={nextSlide}
                Icon={ChevronRightIcon}
                type="large"
                className="pointer-events-auto"
              />
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
